import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

function formatPrice(n) {
  return Number(n || 0).toLocaleString('ko-KR');
}

function buildImageUrl(path) {
  const raw = String(path || '').trim();

  if (!raw) {
    return '/static/default_thumb.jpg';
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  const cleaned = raw
    .replace(/\\/g, '/')
    .replace(/\.\.\//g, '')
    .replace(/\.\//g, '')
    .replace(/^\/+/, '');

  return `/static/${cleaned}`;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Thumb({ imagePath }) {
  const src = buildImageUrl(imagePath);
  const [broken, setBroken] = useState(false);

  return (
    <img
      className="table-cart-thumb"
      src={broken ? '/static/default_thumb.jpg' : src}
      alt=""
      onError={() => setBroken(true)}
    />
  );
}

export default function TableCart() {
  const query = useQuery();
  const navigate = useNavigate();

  const shopId = Number(query.get('shop_id') || 0);
  const tableId = Number(query.get('table_id') || 0);
  const entry = query.get('entry') || '';

  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);

  const [shopName, setShopName] = useState('');
  const [tableName, setTableName] = useState('');
  const [accItems, setAccItems] = useState([]);
  const [accTotal, setAccTotal] = useState(0);
  const [openOrderId, setOpenOrderId] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cnt, setCnt] = useState(0);
  const [canPay, setCanPay] = useState(false);
  const [hasAcc, setHasAcc] = useState(false);
  const [hasCart, setHasCart] = useState(false);
  const [hasAny, setHasAny] = useState(false);
  const [token, setToken] = useState('');

  const entryQs = entry ? `&entry=${encodeURIComponent(entry)}` : '';

  const pageTitle = useMemo(() => {
    return tableName ? `${tableName} 장바구니` : '테이블 장바구니';
  }, [tableName]);

  // =====================================================
  // 누적 주문 화면 표시용 합산
  // 같은 상품 + 같은 옵션 조합 + 같은 단가일 때만 합친다.
  // 실제 주문 데이터와 DB는 변경하지 않는다.
  // =====================================================
  const groupedAccItems = useMemo(() => {
    const groupedMap = new Map();
    const groupedItems = [];

    for (const rawItem of accItems) {
      const options = Array.isArray(rawItem?.options)
        ? rawItem.options
        : Array.isArray(rawItem?.option_items)
          ? rawItem.option_items
          : [];

      const normalizedOptions = options
        .map((option) => ({
          groupId: Number(
            option?.option_group_id ??
            option?.group_id ??
            0
          ),
          itemId: Number(
            option?.option_item_id ??
            option?.item_id ??
            0
          ),
          groupName: String(
            option?.option_group_name ??
            option?.group_name ??
            ''
          ).trim(),
          itemName: String(
            option?.option_item_name ??
            option?.item_name ??
            ''
          ).trim(),
          optionPrice: Number(
            option?.option_price ??
            option?.extra_price ??
            0
          ),
          original: option,
        }))
        .sort((a, b) => {
          if (a.groupId !== b.groupId) {
            return a.groupId - b.groupId;
          }

          if (a.itemId !== b.itemId) {
            return a.itemId - b.itemId;
          }

          if (a.groupName !== b.groupName) {
            return a.groupName.localeCompare(b.groupName);
          }

          if (a.itemName !== b.itemName) {
            return a.itemName.localeCompare(b.itemName);
          }

          return a.optionPrice - b.optionPrice;
        });

      const optionSignature = normalizedOptions
        .map((option) => [
          option.groupId,
          option.itemId,
          option.groupName,
          option.itemName,
          option.optionPrice,
        ].join(':'))
        .join('|');

      const productId = Number(
        rawItem?.product_id ??
        0
      );

      const productName = String(
        rawItem?.name ??
        rawItem?.product_name ??
        ''
      ).trim();

      const optionTotal = Number(
        rawItem?.option_total ??
        rawItem?.option_price ??
        normalizedOptions.reduce(
          (sum, option) => sum + Number(option.optionPrice || 0),
          0
        )
      );

      const productPrice = Number(
        rawItem?.product_price ??
        rawItem?.display_price ??
        Math.max(
          0,
          Number(rawItem?.price ?? 0) - optionTotal
        )
      );

      const unitPrice = Number(
        rawItem?.unit_price ??
        rawItem?.price ??
        (productPrice + optionTotal)
      );

      const quantity = Number(
        rawItem?.qty ??
        rawItem?.quantity ??
        0
      );

      const lineTotal = Number(
        rawItem?.line ??
        rawItem?.line_total ??
        (unitPrice * quantity)
      );

      const groupKey = [
        productId,
        productName,
        productPrice,
        optionTotal,
        unitPrice,
        optionSignature,
      ].join('::');

      const existing = groupedMap.get(groupKey);

      if (existing) {
        existing.qty =
          Number(existing.qty || 0) + quantity;

        existing.quantity =
          Number(existing.quantity || 0) + quantity;

        existing.line =
          Number(existing.line || 0) + lineTotal;

        existing.line_total =
          Number(existing.line_total || 0) + lineTotal;
      } else {
        const groupedItem = {
          ...rawItem,
          name: productName,
          product_name: productName,
          product_id: productId,
          product_price: productPrice,
          display_price: productPrice,
          option_total: optionTotal,
          unit_price: unitPrice,
          price: unitPrice,
          qty: quantity,
          quantity,
          line: lineTotal,
          line_total: lineTotal,
          options: normalizedOptions.map((option) => option.original),
          option_items: normalizedOptions.map((option) => option.original),
          _groupKey: groupKey,
        };

        groupedMap.set(groupKey, groupedItem);
        groupedItems.push(groupedItem);
      }
    }

    return groupedItems;
  }, [accItems]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const applyData = (data) => {
    setShopName(data.shop_name || '');
    setTableName(data.table_name || '');
    setAccItems(Array.isArray(data.acc_items) ? data.acc_items : []);
    setAccTotal(Number(data.acc_total || 0));
    setOpenOrderId(Number(data.open_order_id || 0));
    setItems(Array.isArray(data.items) ? data.items : []);
    setTotal(Number(data.total || 0));
    setCnt(Number(data.cnt || 0));
    setCanPay(Boolean(data.can_pay));
    setHasAcc(Boolean(data.has_acc));
    setHasCart(Boolean(data.has_cart));
    setHasAny(Boolean(data.has_any));
    setToken(String(data.token || ''));
  };

  const loadCart = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/table/cart-data', {
        params: {
          shop_id: shopId,
          table_id: tableId,
          entry: entry || undefined,
        },
        withCredentials: true,
      });
      applyData(res.data || {});
    } catch (err) {
      console.error('테이블 장바구니 조회 실패:', err);
      alert(err.response?.data?.message || '장바구니 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shopId > 0 && tableId > 0) {
      loadCart();
    } else {
      setLoading(false);
      alert('잘못된 접근입니다.');
    }
  }, [shopId, tableId, entry]);

  const postAction = async (action, cartId = null, productId = null) => {
    const formData = new FormData();
    formData.append('action', action);

    if (cartId !== null && Number(cartId) > 0) {
      formData.append('cart_id', String(cartId));
    }

    if (productId !== null && Number(productId) > 0) {
      formData.append('product_id', String(productId));
    }

    try {
      const res = await axios.post('/api/pos/table/cart', formData, {
        withCredentials: true,
      });

      const data = res.data || {};
      if (!data.success) {
        alert(data.message || '처리 실패');
        return false;
      }

      await loadCart();
      return true;
    } catch (err) {
      console.error('장바구니 액션 실패:', err);
      alert(err.response?.data?.message || '처리 실패');
      return false;
    }
  };

  const handleClear = async () => {
    if (!window.confirm('장바구니를 전부 비울까요?')) return;
    await postAction('clear');
  };

  const handlePlaceOrder = async () => {
    if (!canPay) return;
    if (!window.confirm('주문을 전송할까요?\n결제는 나갈 때 카운터에서 진행합니다.')) return;

    setPlacingOrder(true);
    try {
      const res = await axios.post('/api/pos/table_place_order', null, {
        withCredentials: true,
      });

      const data = res.data || {};
      if (!data.success) {
        alert(data.message || '주문 전송 실패');
        return;
      }

      setToken(String(data.token || token || ''));
      alert('주문이 전송되었습니다!');
      navigate(`/table/order?shop_id=${shopId}&table_id=${tableId}${entryQs}`);
    } catch (err) {
      console.error('주문 전송 실패:', err);
      alert(err.response?.data?.message || '주문 전송 실패');
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}></div>;
  }

  return (
    <>
      <style>{`
        .table-cart-page {
          background: #eee;
          min-height: 100vh;
          padding-bottom: 120px;
        }
        .table-cart-card {
          border: 0;
          border-radius: 6px;
        }
        .table-cart-thumb {
          width: 72px;
          height: 72px;
          object-fit: cover;
          border-radius: 10px;
          background: #eee;
          flex: 0 0 72px;
        }
        .table-cart-sticky-bar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 20;
          background: #fff;
          border-top: 1px solid #eee;
        }
        .table-cart-qty-btn {
          width: 26px;
          height: 26px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div className="table-cart-page">
        <div className="container py-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button
              type="button"
              className="btn text-secondary btn-lg ps-0"
              onClick={() => navigate(`/table/order?shop_id=${shopId}&table_id=${tableId}${entryQs}`)}
            >
              ← 계속 담기
            </button>
            <div>
              {/* <div className="text-muted small">{shopName}</div> */}
              <h4 className="fw-bold m-0">{tableName || tableId} 장바구니</h4>
            </div>
          </div>

          {!hasAny ? (
            <div className="alert alert-warning">장바구니가 비었습니다. 메뉴에서 담아주세요.</div>
          ) : (
            <>
              {hasAcc && (
                <div className="card table-cart-card mb-3">
                  <div className="card-body">
                    <div className="fw-bold mb-2">누적 주문 내역(미결제)</div>
                    <div className="text-muted small mb-2">주문번호 #{openOrderId}</div>

                    {groupedAccItems.map((it, idx) => {
                      const options = Array.isArray(it.options) ? it.options : [];
                      const productPrice = Number(it.product_price ?? it.display_price ?? it.price ?? 0);
                      const optionTotal = Number(it.option_total ?? it.option_price ?? 0);
                      const unitPrice = Number(it.unit_price ?? it.price ?? (productPrice + optionTotal));
                      const quantity = Number(it.qty ?? it.quantity ?? 0);
                      const lineTotal = Number(it.line ?? it.line_total ?? (unitPrice * quantity));

                      return (
                        <div
                          className="d-flex gap-3 align-items-center py-2 border-top"
                          key={it._groupKey || `${it.order_item_id || it.product_id}-${idx}`}
                        >
                          <Thumb imagePath={it.image_path} />

                          <div className="flex-grow-1">
                            <div className="fw-bold">{it.name}</div>

                            {options.length > 0 && (
                              <div className="mt-1">
                                {options.map((opt, optIdx) => {
                                  const groupName =
                                    opt.option_group_name ||
                                    opt.group_name ||
                                    '';
                                  const itemName =
                                    opt.option_item_name ||
                                    opt.item_name ||
                                    '';
                                  const optionPrice =
                                    Number(opt.option_price ?? opt.extra_price ?? 0);

                                  return (
                                    <div
                                      className="text-muted"
                                      style={{
                                        fontSize: '0.74rem',
                                        lineHeight: '1.2'
                                      }}
                                      key={`${it.order_item_id || it.product_id}-${optIdx}`}
                                    >
                                      {groupName ? `${groupName}: ` : ''}
                                      {itemName}
                                      {optionPrice !== 0
                                        ? ` (${optionPrice > 0 ? '+' : ''}${formatPrice(optionPrice)}원)`
                                        : ''}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="text-muted small mt-1">
                               {/* 상품 {formatPrice(productPrice)}원
                              {optionTotal > 0
                                ? ` + 옵션 ${formatPrice(optionTotal)}원`
                                : ''}
                              {' · '} */}
                              {quantity}개
                            </div>
                          </div>

                          <div className="fw-bold align-self-start">{formatPrice(lineTotal)}원</div>
                        </div>
                      );
                    })}

                    <div className="border-top mt-0 pt-3">
                      <div className="d-flex justify-content-between">
                        <div className="fw-bold">누적 합계</div>
                        <div className="fw-bold fs-5">
                          {formatPrice(accTotal)}원
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {hasCart ? (
                <div className="card table-cart-card mb-3">
                  <div className="card-body">
                    <div className="fw-bold mb-2">추가 주문</div>

                    {items.map((it, idx) => {
                      const options = Array.isArray(it.options) ? it.options : [];
                      const originalPrice = Number(
                        it.original_price ??
                        it.normal_price ??
                        it.price ??
                        0
                      );
                      const displayPrice = Number(
                        it.display_price ??
                        it.product_price ??
                        it.price ??
                        0
                      );
                      const optionTotal = Number(
                        it.option_total ??
                        it.option_price ??
                        0
                      );
                      const unitPrice = Number(
                        it.unit_price ??
                        (displayPrice + optionTotal)
                      );
                      const quantity = Number(
                        it.qty ??
                        it.quantity ??
                        0
                      );
                      const lineTotal = Number(
                        it.line ??
                        it.line_total ??
                        (unitPrice * quantity)
                      );
                      const isSale =
                        Boolean(it.is_sale) ||
                        (
                          originalPrice > 0 &&
                          displayPrice > 0 &&
                          displayPrice < originalPrice
                        );

                      return (
                        <div
                          className="d-flex gap-3 align-items-center py-2 border-top"
                          key={`${it.cart_id || it.product_id}-${idx}`}
                        >
                          <Thumb imagePath={it.image_path} />

                          <div className="flex-grow-1">
                            <div className="fw-bold">{it.name}</div>

                            {options.length > 0 && (
                              <div className="mt-1">
                                {options.map((opt, optIdx) => {
                                  const groupName =
                                    opt.option_group_name ||
                                    opt.group_name ||
                                    '';
                                  const itemName =
                                    opt.option_item_name ||
                                    opt.item_name ||
                                    '';
                                  const optionPrice =
                                    Number(opt.option_price ?? opt.extra_price ?? 0);

                                  return (
                                    <div
                                      className="text-muted small"
                                      key={`${it.cart_id || it.product_id}-${optIdx}`}
                                    >
                                      · {groupName ? `${groupName}: ` : ''}
                                      {itemName}
                                      {optionPrice !== 0
                                        ? ` (${optionPrice > 0 ? '+' : ''}${formatPrice(optionPrice)}원)`
                                        : ''}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* <div className="mt-1">
                              {isSale && (
                                <div
                                  className="text-muted small"
                                  style={{ textDecoration: 'line-through' }}
                                >
                                  정상가 {formatPrice(originalPrice)}원
                                </div>
                              )}

                              <div className={isSale ? 'fw-bold text-danger' : 'fw-bold text-muted'}>
                                상품 {formatPrice(displayPrice)}원
                              </div>

                              {optionTotal > 0 && (
                                <div className="text-primary small">
                                  옵션 +{formatPrice(optionTotal)}원
                                </div>
                              )}

                              <div className="text-muted small">
                                단가 {formatPrice(unitPrice)}원
                              </div>
                            </div> */}

                            <div className="d-flex align-items-center gap-2 mt-2">
                              <button
                                className="btn btn-outline-secondary table-cart-qty-btn"
                                type="button"
                                onClick={() =>
                                  postAction(
                                    'dec',
                                    Number(it.cart_id || 0),
                                    Number(it.product_id || 0)
                                  )
                                }
                              >
                                -
                              </button>

                              <div className="fw-bold">{quantity}</div>

                              <button
                                className="btn btn-outline-secondary table-cart-qty-btn"
                                type="button"
                                onClick={() =>
                                  postAction(
                                    'inc',
                                    Number(it.cart_id || 0),
                                    Number(it.product_id || 0)
                                  )
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <div className="fw-bold text-end">
                            {formatPrice(lineTotal)}원
                            <br />
                            <br />

                            <button
                              className="btn btn-dark btn-sm"
                              type="button"
                              onClick={() =>
                                postAction(
                                  'remove',
                                  Number(it.cart_id || 0),
                                  Number(it.product_id || 0)
                                )
                              }
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="border-top mt-0 pt-3">
                      <div className="d-flex justify-content-between">
                        <div className="fw-bold">추가 주문 합계</div>
                        <div className="fw-bold fs-5">
                          {formatPrice(total)}원
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-2">
                      <button className="btn btn-dark btn-sm" type="button" onClick={handleClear}>
                        전체 비우기
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-light">
                  현재 추가 주문 내역이 없습니다.
                </div>
              )}
            </>
          )}
        </div>

        <div className="table-cart-sticky-bar">
          <div className="container py-2">
            <button
              className={`btn btn-primary p-2 w-100 ${canPay ? '' : 'disabled'}`}
              id="btnPlaceOrder"
              type="button"
              disabled={!canPay || placingOrder}
              onClick={handlePlaceOrder}
            >
              {placingOrder ? '전송중...' : '주문 전송(후결제)'}
            </button>

            <div className="text-muted small mt-1 mx-1">
              주문만 먼저 전송되고, 결제는 카운터에서 진행합니다.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}